"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShieldNote = void 0;
const circomlibjs_1 = require("circomlibjs");
const utils_1 = require("ethereum-cryptography/utils");
const utils_2 = require("../utils");
const bytes_1 = require("../utils/bytes");
const note_util_1 = require("./note-util");
class ShieldNote {
    constructor(masterPublicKey, random, value, tokenData) {
        (0, note_util_1.assertValidNoteRandom)(random);
        (0, note_util_1.assertValidNoteToken)(tokenData, value);
        this.masterPublicKey = masterPublicKey;
        this.random = random;
        this.tokenData = tokenData;
        this.tokenHash = (0, note_util_1.getTokenDataHash)(tokenData);
        this.value = value;
        this.notePublicKey = ShieldNote.getNotePublicKey(masterPublicKey, random);
    }
    /**
     * Used to generate a shieldPrivateKey by signing this message with public wallet.
     * After shielding, the shieldPrivateKey can then be used to get the 0zk address of the receiver.
     */
    static getShieldPrivateKeySignatureMessage() {
        // DO NOT MODIFY THIS CONSTANT.
        return 'RAILGUN_SHIELD';
    }
    static getNotePublicKey(masterPublicKey, random) {
        return (0, circomlibjs_1.poseidon)([masterPublicKey, (0, bytes_1.hexToBigInt)(random)]);
    }
    static getShieldNoteHash(notePublicKey, tokenHash, valueLessFee) {
        return (0, circomlibjs_1.poseidon)([notePublicKey, (0, bytes_1.hexToBigInt)(tokenHash), valueLessFee]);
    }
    static decryptRandom(encryptedBundle, sharedKey) {
        const hexlified0 = (0, bytes_1.hexlify)(encryptedBundle[0]);
        const hexlified1 = (0, bytes_1.hexlify)(encryptedBundle[1]);
        const decrypted = utils_2.AES.decryptGCM({
            iv: hexlified0.slice(0, 32),
            tag: hexlified0.slice(16, 64),
            data: [hexlified1.slice(0, 32)],
        }, sharedKey)[0];
        return (0, bytes_1.hexlify)(decrypted);
    }
    /**
     * Gets JSON serialized version of note
     * @param viewingPrivateKey - viewing private key for decryption
     * @param forContract - if we should 0x prefix the hex strings to make them ethers compatible
     * @returns serialized note
     */
    async serialize(shieldPrivateKey, receiverViewingPublicKey) {
        // Get shared key
        const sharedKey = await (0, utils_2.getSharedSymmetricKey)(shieldPrivateKey, receiverViewingPublicKey);
        if (!sharedKey) {
            throw new Error('Could not generated shared symmetric key for shielding.');
        }
        // Encrypt random
        const encryptedRandom = utils_2.AES.encryptGCM([this.random], sharedKey);
        // Encrypt receiver public key
        const encryptedReceiver = utils_2.AES.encryptCTR([(0, utils_1.bytesToHex)(receiverViewingPublicKey)], shieldPrivateKey);
        const shieldKey = (0, utils_1.bytesToHex)(await (0, utils_2.getPublicViewingKey)(shieldPrivateKey));
        // Construct ciphertext
        const ciphertext = {
            encryptedBundle: [
                (0, bytes_1.hexlify)(`${encryptedRandom.iv}${encryptedRandom.tag}`, true),
                (0, bytes_1.hexlify)((0, bytes_1.combine)([...encryptedRandom.data, encryptedReceiver.iv]), true),
                (0, bytes_1.hexlify)((0, bytes_1.combine)(encryptedReceiver.data), true),
            ],
            shieldKey: (0, bytes_1.hexlify)(shieldKey, true),
        };
        return {
            preimage: {
                npk: (0, bytes_1.nToHex)(this.notePublicKey, bytes_1.ByteLength.UINT_256, true),
                token: this.tokenData,
                value: this.value,
            },
            ciphertext,
        };
    }
}
exports.ShieldNote = ShieldNote;
